import { createI18n } from 'vue-i18n'
import en from '@/locales/en'
import zh from '@/locales/zh'

const LOCALE_KEY = 'manufacturing.locale'

export type AppLocale = 'en' | 'zh'

function initialLocale(): AppLocale {
  try {
    const stored = window.localStorage.getItem(LOCALE_KEY)
    if (stored === 'en' || stored === 'zh') {
      return stored
    }
  } catch {
    /* fall through to the browser preference */
  }
  return navigator.language.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: 'en',
  messages: { en, zh }
})

export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  document.documentElement.lang = locale
  try {
    window.localStorage.setItem(LOCALE_KEY, locale)
  } catch {
    /* ignore: the choice then simply does not survive a reload */
  }
}

/**
 * Turns a backend error code into a localized message.
 *
 * Codes are the contract, wording is ours — so an unmapped code falls back to whatever detail the
 * server sent rather than showing a blank or the raw code.
 */
export function translateErrorCode(code: string, serverMessage?: string): string {
  // Bracketed, because an error code contains dots and vue-i18n reads a dot as a path separator:
  // `error.auth.required` looks for error → auth → required and finds nothing, so every mapping in the
  // error block would be dead and the server's English detail would show even in Chinese.
  const key = `error['${code}']`
  if (i18n.global.te(key)) {
    return i18n.global.t(key)
  }
  return serverMessage || i18n.global.t('error.unknown')
}
